import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
const POLL_MS = 2500;
export function useJobsList(opts = {}) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    // El último snapshot vive en un ref para que el bucle de polling lo lea
    // SIN tener `data` en las dependencias del effect (eso causaba que cada
    // refresh re-montase el effect y multiplicara los timers → loop infinito).
    const dataRef = useRef(null);
    const refresh = useCallback(async () => {
        try {
            const params = {};
            if (opts.kind)
                params.kind = opts.kind;
            if (opts.limit)
                params.limit = opts.limit;
            const r = await api.get('/jobs', { params });
            dataRef.current = r.data;
            setData(r.data);
            setError(null);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }, [opts.kind, opts.limit]);
    // UN solo bucle de polling, recreado SÓLO si cambia `refresh` (kind/limit).
    // Cadencia adaptativa leyendo el ref: 2.5 s si hay jobs vivos, 6.25 s si
    // todos terminaron. Respeta visibilitychange. Se reprograma a sí mismo con
    // setTimeout encadenado y se cancela limpio en el cleanup mediante un flag.
    useEffect(() => {
        let cancelled = false;
        let handle;
        const loop = async () => {
            if (cancelled)
                return;
            if (document.visibilityState === 'visible') {
                await refresh();
            }
            if (cancelled)
                return;
            const snap = dataRef.current;
            const hasPending = snap?.items?.some((j) => j.status === 'queued' || j.status === 'running');
            const next = hasPending || snap === null ? POLL_MS : POLL_MS * 2.5;
            handle = window.setTimeout(() => void loop(), next);
        };
        void loop();
        return () => {
            cancelled = true;
            if (handle !== undefined)
                window.clearTimeout(handle);
        };
    }, [refresh]);
    return { jobs: data?.items ?? [], total: data?.total ?? 0, error, refresh };
}
export async function deleteJob(id) {
    await api.delete(`/jobs/${id}`);
}
// Control de operaciones en curso. pause/resume sólo aplican a jobs distribuidos
// (orchestrate); cancel vale para cualquier job en curso.
export async function pauseJob(id) {
    await api.post(`/jobs/${id}/pause`);
}
export async function resumeJob(id) {
    await api.post(`/jobs/${id}/resume`);
}
export async function cancelJob(id) {
    await api.post(`/jobs/${id}/cancel`);
}
/** ¿Se puede pausar/reanudar este job? (distribuidos en curso). */
export function canPauseResume(job) {
    return ((job.kind === 'orchestrate' || job.kind === 'bdecode') &&
        (job.status === 'running' || job.status === 'paused'));
}
/** ¿Se puede cancelar? (cualquier job aún no terminado). */
export function canCancel(job) {
    return ['queued', 'running', 'paused'].includes(job.status);
}
export function outputUrl(id) {
    // Reutiliza la baseURL configurada en el axios client.
    const base = api.defaults.baseURL ?? '/api/v1';
    return `${base.replace(/\/$/, '')}/jobs/${id}/output`;
}
async function downloadWithAuth(url, filename) {
    const token = localStorage.getItem('gestiora.token');
    const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok)
        throw new Error(`download_${r.status}`);
    const blob = await r.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}
const apiBase = () => (api.defaults.baseURL ?? '/api/v1').replace(/\/$/, '');
/** ¿La operación de un job orchestrate fue encode (→mp4) o decode (→bin)? */
function orchestrateOp(job) {
    const op = (job.result?.operation ?? job.params?.operation);
    return op === 'decode' ? 'decode' : 'encode';
}
export async function downloadOutput(job) {
    const isVideo = job.kind === 'encode' ||
        job.kind === 'calibrate' ||
        job.kind === 'fetch' ||
        (job.kind === 'orchestrate' && orchestrateOp(job) === 'encode');
    const ext = isVideo ? 'mp4' : 'bin';
    await downloadWithAuth(`${apiBase()}/jobs/${job.id}/output`, `gestiora-${job.kind}-${job.id.slice(0, 8)}.${ext}`);
}
export async function downloadInput(job) {
    const ext = job.kind === 'decode' || (job.kind === 'orchestrate' && orchestrateOp(job) === 'decode')
        ? 'mp4'
        : 'bin';
    await downloadWithAuth(`${apiBase()}/jobs/${job.id}/input`, `input-${job.kind}-${job.id.slice(0, 8)}.${ext}`);
}
export async function downloadMetadata(job) {
    // /metadata devuelve JSON; lo guardamos pretty-printed.
    const token = localStorage.getItem('gestiora.token');
    const r = await fetch(`${apiBase()}/jobs/${job.id}/metadata`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok)
        throw new Error(`metadata_${r.status}`);
    const obj = await r.json();
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `gestiora-${job.kind}-${job.id.slice(0, 8)}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
}
/** Consulta /jobs/eta?kind=… para mostrar una estimación ANTES de lanzar.
 *  Refresca cuando cambia `kind`. Devuelve null mientras carga o si no
 *  hay histórico suficiente. */
export function useEta(kind) {
    const [eta, setEta] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        setLoading(true);
        api
            .get('/jobs/eta', { params: { kind } })
            .then((r) => { if (alive)
            setEta(r.data.eta_seconds); })
            .catch(() => { if (alive)
            setEta(null); })
            .finally(() => { if (alive)
            setLoading(false); });
        return () => { alive = false; };
    }, [kind]);
    return { eta, loading };
}
export function formatEta(seconds) {
    if (seconds === null || seconds === undefined || seconds < 0)
        return '—';
    if (seconds < 1)
        return '<1s';
    if (seconds < 60)
        return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m < 60)
        return s === 0 ? `${m}min` : `${m}min ${s}s`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${mm}min`;
}
export function useQueueStats() {
    const [stats, setStats] = useState(null);
    const fetchStats = useCallback(async () => {
        try {
            const r = await api.get('/jobs/queue-stats');
            setStats(r.data);
        }
        catch {
            // Silencioso en caso de error de red/sesión
        }
    }, []);
    useEffect(() => {
        let alive = true;
        const tick = async () => {
            if (!alive)
                return;
            if (document.visibilityState === 'visible') {
                await fetchStats();
            }
        };
        void tick();
        const i = setInterval(() => void tick(), 5000);
        return () => {
            alive = false;
            clearInterval(i);
        };
    }, [fetchStats]);
    return stats;
}
